import { useFormik } from 'formik';
import React, { ChangeEvent, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup'
import { AddApplicationInterface, addApplicationInitialValues } from '../../../modules/accounts/components/settings/SettingsModel';
import { add_application } from '../api/applicationsApi';
import { toast } from 'react-toastify';
import { validateImage } from '../../../helper-functions/ImageValidation';

const applicationSchema = Yup.object().shape({
    name: Yup.string().required("Application Name is required"),
    api_url: Yup.string().required("API URL is required"),
    server_url:Yup.string().required("Server URL is required"),
    website_url: Yup.string().required("Website URL is required"),
    frontend_domain_prefix: Yup.string().required("Frontend Domain Prefix is required"),
    api_domain_prefix: Yup.string().required("API Domain Prefix is required"),
})
function AddApplication() {
    const [loading, setLoading] = useState(false);
    const [logo, setLogo] = useState<File | null>(null);
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [favIcon, setFavIcon] = useState<any>("");
    const [whiteLogo, setWhiteLogo] = useState<any>("");
    const [coloredLogo, setColoredLogo] = useState<any>("");
    const mutation = useMutation(add_application, {
        onSettled: (data, error: any) => {
            setLoading(false); // Reset loading state

            if (!error) {
                toast.success(data?.message || 'Application added successfully!', {
                    position: 'top-right',
                    autoClose: 3000,
                });
                navigate('/applications/applications-list');
                queryClient.invalidateQueries('organisations');
            } else {
                const errorMessage = error?.response?.data?.message || error?.message || 'Something went wrong';
                toast.error(errorMessage, {
                    position: 'top-right',
                    autoClose: 3000,
                });
            }
        },
    });

    const formik = useFormik<AddApplicationInterface>({
        initialValues: addApplicationInitialValues,
        validationSchema: applicationSchema,
        onSubmit: (values) => {
            setLoading(true);

            // Create a FormData object to handle file uploads
            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('status', values.status.toString());
            formData.append('api_url', values.api_url);
            formData.append('frontend_domain_prefix', values.frontend_domain_prefix);
            formData.append('api_domain_prefix', values.api_domain_prefix);
            formData.append('server_url', values.api_url);
            formData.append('website_url', values.website_url || '');
            formData.append('description', values.description);
            if (whiteLogo) formData.append('white_logo', whiteLogo);
            if (coloredLogo) formData.append('colored_logo', coloredLogo);
            if (favIcon) formData.append('favicon', favIcon);
            mutation.mutate(formData);
        },
    });
    const handleImageChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        logoType:string,
        MAX_FILE_SIZE: number,
        ALLOWED_TYPES: string[],
        MAX_DIMENSIONS: { [key: string]: { width: number; height: number } }
      ) => {
        const newFile = e.target.files?.[0];
        if (newFile) {
          // Call the validation function
          validateImage(
            newFile,
            logoType,
            (validFileUrl) => {
              // If valid, update the state with the image URL
              if (logoType === 'color_logo') {
                setColoredLogo(newFile);
              } else if (logoType === 'white_logo') {
                setWhiteLogo(newFile);
              } else if (logoType === 'favicon') {
                setFavIcon(newFile);
              }
            },
            MAX_FILE_SIZE, // File size limit in MB
            ALLOWED_TYPES, // Allowed file types
            MAX_DIMENSIONS // Maximum dimensions per logo type
          );
        }
      };
    return (

        <div className='card mb-5 mb-xl-10'>
            <div
                className='card-header border-0'
            >
                <div className='card-title m-0'>
                    <h3 className='fw-bolder m-0'>Create Application</h3>
                </div>
            </div>
            <div id='kt_account_profile_details' className='collapse show'>
                <form onSubmit={formik.handleSubmit} noValidate className='form'>
                    <div className='card-body border-top p-9'>
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Name</label>
                            <div className='col-lg-8 fv-row'>
                                <input
                                    type='text'
                                    className='form-control form-control-lg form-control-solid'
                                    placeholder="Name"
                                    {...formik.getFieldProps('name')}
                                />
                                {formik.touched.name && formik.errors.name && (
                                    <div className='fv-plugins-message-container'>
                                        <div className='fv-help-block'>{formik.errors.name}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Frontend Domain Prefix</label>
                            <div className='col-lg-8 fv-row'>
                                <input
                                    type='text'
                                    className='form-control form-control-lg form-control-solid'
                                    placeholder="Frontend Domain Prefix"
                                    {...formik.getFieldProps('frontend_domain_prefix')}
                                />
                                {formik.touched.frontend_domain_prefix && formik.errors.frontend_domain_prefix && (
                                    <div className='fv-plugins-message-container'>
                                        <div className='fv-help-block'>{formik.errors.frontend_domain_prefix}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label required fw-bold fs-6'>API Domain Prefix</label>
                            <div className='col-lg-8 fv-row'>
                                <input
                                    type='text'
                                    className='form-control form-control-lg form-control-solid'
                                    placeholder="API Domain Prefix"
                                    {...formik.getFieldProps('api_domain_prefix')}
                                />
                                {formik.touched.api_domain_prefix && formik.errors.api_domain_prefix && (
                                    <div className='fv-plugins-message-container'>
                                        <div className='fv-help-block'>{formik.errors.api_domain_prefix}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Website URL</label>
                            <div className='col-lg-8 fv-row'>
                                <input
                                    type='text'
                                    className='form-control form-control-lg form-control-solid'
                                    placeholder="Website URL"
                                    {...formik.getFieldProps('website_url')}
                                />
                                {formik.touched.website_url && formik.errors.website_url && (
                                    <div className='fv-plugins-message-container'>
                                        <div className='fv-help-block'>{formik.errors.website_url}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label required fw-bold fs-6'>API URL</label>
                            <div className='col-lg-8 fv-row'>
                                <input
                                    type='text'
                                    className='form-control form-control-lg form-control-solid'
                                    placeholder="API URL"
                                    {...formik.getFieldProps('api_url')}
                                />
                                {formik.touched.api_url && formik.errors.api_url && (
                                    <div className='fv-plugins-message-container'>
                                        <div className='fv-help-block'>{formik.errors.api_url}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Server URL</label>
                            <div className='col-lg-8 fv-row'>
                                <input
                                    type='text'
                                    className='form-control form-control-lg form-control-solid'
                                    placeholder="Server URL"
                                    {...formik.getFieldProps('server_url')}
                                />
                                {formik.touched.server_url && formik.errors.server_url && (
                                    <div className='fv-plugins-message-container'>
                                        <div className='fv-help-block'>{formik.errors.server_url}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label fw-bold fs-6'>
                                <span>Description</span>
                            </label>

                            <div className='col-lg-8 fv-row'>
                                <input
                                    type='tel'
                                    className='form-control form-control-lg form-control-solid'
                                    placeholder='Description'
                                    {...formik.getFieldProps('description')}
                                />
                            </div>
                        </div>
                        <div className='row mb-6'>
                        <label className='col-lg-4 col-form-label fw-bold fs-6'>Favicon</label>
                        <div className='col-lg-8 fv-row'>
                            <input
                                type='file'
                                onChange={(e) => handleImageChange(
                                    e,
                                    'favicon',
                                    2, // Max file size (2MB)
                                    ['image/jpeg', 'image/jpg', 'image/png'], // Allowed types
                                    {
                                      favicon: { width: 100, height: 100 }
                                    } // Max dimensions
                                  )}
                                  accept="image/jpeg, image/jpg, image/png"
                                  name='Favicon'
                                className='form-control form-control-lg form-control-solid'
                            />
                           <div className='text-danger mt-2'>
                Recommended size: 100 x 100 px, max file size: 2MB, and accepted file types: jpg, jpeg, or png only.
              </div>
                            </div>
                        </div>
                        <div className='row mb-6'>
                        <label className='col-lg-4 col-form-label fw-bold fs-6'>White Logo</label>
                        <div className='col-lg-8 fv-row'>
                            <input
                                type='file'
                                onChange={(e) => handleImageChange(
                                    e,
                                    'white_logo',
                                    2, // Max file size (2MB)
                                    ['image/jpeg', 'image/jpg', 'image/png'], // Allowed types
                                    {
                                        white_logo: { width: 400, height: 400 }
                                    } // Max dimensions
                                  )}
                                  accept="image/jpeg, image/jpg, image/png"
                                className='form-control form-control-lg form-control-solid'
                            />
                            <div className='text-danger mt-2'>
                Recommended size: 400 x 400 px, max file size: 2MB, and accepted file types: jpg, jpeg, or png only.
              </div>
                            </div>
                        </div>
                        <div className='row mb-6'>
                        <label className='col-lg-4 col-form-label fw-bold fs-6'>Coloured Logo</label>
                        <div className='col-lg-8 fv-row'>
                            <input
                                type='file'
                                className='form-control form-control-lg form-control-solid'
                                onChange={(e) => handleImageChange(
                                    e,
                                    'color_logo',
                                    2, // Max file size (2MB)
                                    ['image/jpeg', 'image/jpg', 'image/png'], // Allowed types
                                    {
                                        color_logo: { width: 400, height: 400 }
                                    } // Max dimensions
                                  )}
                                  accept="image/jpeg, image/jpg, image/png"
                                  name='Coloured Logo'
                            />
                            <div className='text-danger mt-2'>
                Recommended size: 400 x 400 px, max file size: 2MB, and accepted file types: jpg, jpeg, or png only.
              </div>
                            </div>
                        </div>
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label fw-bold fs-6'>
                                <span>Status</span>
                            </label>
                            <div className='col-lg-8 fv-row'>
                                <select
                                    className='form-select form-select-solid form-select-lg fw-bold'
                                    {...formik.getFieldProps('status')}
                                >
                                    <option value='1'>Active</option>
                                    <option value='0'>Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className='card-footer d-flex justify-content-end py-6 px-9'>
                    <button
                            data-toggle="tooltip" data-placement="bottom" title="Navigate to Application List"
                            className='btn btn-light-dark mx-3'
                            onClick={() => navigate('/applications/applications-list')}
                        >
                            Cancel
                        </button>
                        <button data-toggle="tooltip" data-placement="bottom" title="Add New Application" type='submit' className='btn btn-primary' disabled={loading}>
                            {!loading && 'Add Application'}
                            {loading && (
                                <span className='indicator-progress' style={{ display: 'block' }}>
                                    Please wait...{' '}
                                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                            )}
                        </button>
                    </div>
                </form>
            </div >
        </div >
    )
}

export default AddApplication